

const TABELA_T_STUDENT_80: { [grausLiberdade: number]: number } = {
  1: 3.078,
  2: 1.886,
  3: 1.638,
  4: 1.533,
  5: 1.476,
  6: 1.440,
  7: 1.415,
  8: 1.397,
  9: 1.383,
  10: 1.372,
  11: 1.363,
  12: 1.356,
  13: 1.350,
  14: 1.345,
  15: 1.341,
  16: 1.337,
  17: 1.333,
  18: 1.330,
  19: 1.328,
  20: 1.325,
  21: 1.323,
  22: 1.321,
  23: 1.319,
  24: 1.318,
  25: 1.316,
  26: 1.315,
  27: 1.314,
  28: 1.313,
  29: 1.311,
  30: 1.310,
  40: 1.303,
  50: 1.299,
  60: 1.296,
  80: 1.292,
  100: 1.290,
  120: 1.289,
};

export function obterTStudent(grausLiberdade: number, nivelConfianca: number = 0.80): number {
  if (TABELA_T_STUDENT_80[grausLiberdade]) {
    return TABELA_T_STUDENT_80[grausLiberdade];
  }

  const chaves = Object.keys(TABELA_T_STUDENT_80)
    .map(Number)
    .sort((a, b) => a - b);

  let inferior: number = chaves[0] || 1;
  let superior: number = chaves[chaves.length - 1] || 120;

  for (let i = 0; i < chaves.length - 1; i++) {
    const chaveAtual = chaves[i];
    const proximaChave = chaves[i + 1];
    if (chaveAtual !== undefined && proximaChave !== undefined) {
      if (grausLiberdade >= chaveAtual && grausLiberdade <= proximaChave) {
        inferior = chaveAtual;
        superior = proximaChave;
        break;
      }
    }
  }

  if (grausLiberdade > superior) {
    return 1.282;
  }

  const tInferior = TABELA_T_STUDENT_80[inferior] || 1.282;
  const tSuperior = TABELA_T_STUDENT_80[superior] || 1.282;
  const proporcao = (grausLiberdade - inferior) / (superior - inferior);

  return tInferior + proporcao * (tSuperior - tInferior);
}

export interface EstatisticasCompletas {
  
  numeroElementos: number;

  media: number;

  desvioPadrao: number;

  coeficienteVariacao: number;

  tStudent: number;

  limiteInferior: number;

  limiteSuperior: number;

  intervaloConfiabilidade: number;

  amplitudeTotal: number;

  valorMinimo: number;

  valorMaximo: number;
}

export function calcularEstatisticasCompletas(valores: number[]): EstatisticasCompletas {
  const n = valores.length;

  if (n === 0) {
    return {
      numeroElementos: 0,
      media: 0,
      desvioPadrao: 0,
      coeficienteVariacao: 0,
      tStudent: 0,
      limiteInferior: 0,
      limiteSuperior: 0,
      intervaloConfiabilidade: 0,
      amplitudeTotal: 0,
      valorMinimo: 0,
      valorMaximo: 0,
    };
  }

  const media = valores.reduce((acc, val) => acc + val, 0) / n;

  let desvioPadrao = 0;
  if (n > 1) {
    const variancia = valores.reduce((acc, val) => acc + Math.pow(val - media, 2), 0) / (n - 1);
    desvioPadrao = Math.sqrt(variancia);
  }

  const coeficienteVariacao = media !== 0 ? (desvioPadrao / media) * 100 : 0;

  const grausLiberdade = n - 1;
  const tStudent = obterTStudent(grausLiberdade, 0.80);

  // NBR 14653: Limites fixos de ±30% em relação à média
  // Critério de precisão/fundamentação normativo
  const limiteInferior = media * 0.7;  // -30%
  const limiteSuperior = media * 1.3;  // +30%

  // Calcula IC apenas para referência estatística, não para definir limites
  const erroEstatistico = n > 1 ? tStudent * (desvioPadrao / Math.sqrt(n)) : 0;
  const intervaloConfiabilidade = media !== 0 ? erroEstatistico / media : 0;

  const valorMinimo = Math.min(...valores);
  const valorMaximo = Math.max(...valores);
  const amplitudeTotal = valorMaximo - valorMinimo;

  return {
    numeroElementos: n,
    media,
    desvioPadrao,
    coeficienteVariacao,
    tStudent,
    limiteInferior,
    limiteSuperior,
    intervaloConfiabilidade,
    amplitudeTotal,
    valorMinimo,
    valorMaximo,
  };
}

export enum GrauFundamentacao {
  I = 'I',
  II = 'II',
  III = 'III',
}

export enum GrauPrecisao {
  I = 'I',
  II = 'II',
  III = 'III',
}

export interface CriteriosGrauFundamentacao {
  grau: GrauFundamentacao;
  descricao: string;
  atendido: boolean;
  detalhes: string;
}

export function calcularGrauFundamentacao(
  numeroAmostras: number,
  temInferenciaEstatistica: boolean = true,
  temVariaveisQuantitativas: boolean = true
): CriteriosGrauFundamentacao {
  if (
    numeroAmostras >= 6 &&
    temVariaveisQuantitativas &&
    temInferenciaEstatistica
  ) {
    return {
      grau: GrauFundamentacao.III,
      descricao: 'Grau de Fundamentação III',
      atendido: true,
      detalhes: `${numeroAmostras} amostras, modelo quantitativo com inferência estatística`,
    };
  }

  if (numeroAmostras >= 4) {
    return {
      grau: GrauFundamentacao.II,
      descricao: 'Grau de Fundamentação II',
      atendido: true,
      detalhes: `${numeroAmostras} amostras com análise estatística`,
    };
  }

  if (numeroAmostras >= 3) {
    return {
      grau: GrauFundamentacao.I,
      descricao: 'Grau de Fundamentação I',
      atendido: true,
      detalhes: `${numeroAmostras} amostras com homogeneização`,
    };
  }

  return {
    grau: GrauFundamentacao.I,
    descricao: 'Grau de Fundamentação insuficiente',
    atendido: false,
    detalhes: `Apenas ${numeroAmostras} amostra(s). Mínimo: 3 para Grau I`,
  };
}

export interface CriteriosGrauPrecisao {
  grau: GrauPrecisao;
  descricao: string;
  atendido: boolean;
  amplitude: number;
  detalhes: string;
}

export function calcularGrauPrecisao(
  intervaloConfiabilidade: number
): CriteriosGrauPrecisao {
  const amplitudePercentual = intervaloConfiabilidade * 100;

  if (amplitudePercentual <= 15) {
    return {
      grau: GrauPrecisao.III,
      descricao: 'Grau de Precisão III (Rigoroso)',
      atendido: true,
      amplitude: amplitudePercentual,
      detalhes: `Amplitude de ${amplitudePercentual.toFixed(2).replace('.', ',')}% (≤ 15%)`,
    };
  }

  if (amplitudePercentual <= 30) {
    return {
      grau: GrauPrecisao.II,
      descricao: 'Grau de Precisão II (Normal)',
      atendido: true,
      amplitude: amplitudePercentual,
      detalhes: `Amplitude de ${amplitudePercentual.toFixed(2).replace('.', ',')}% (≤ 30%)`,
    };
  }

  if (amplitudePercentual <= 40) {
    return {
      grau: GrauPrecisao.I,
      descricao: 'Grau de Precisão I (Expedito)',
      atendido: true,
      amplitude: amplitudePercentual,
      detalhes: `Amplitude de ${amplitudePercentual.toFixed(2).replace('.', ',')}% (≤ 40%)`,
    };
  }

  return {
    grau: GrauPrecisao.I,
    descricao: 'Grau de Precisão insuficiente',
    atendido: false,
    amplitude: amplitudePercentual,
    detalhes: `Amplitude de ${amplitudePercentual.toFixed(2).replace('.', ',')}% (> 40%). Recomenda-se aumentar amostra`,
  };
}

export function validarCoeficienteVariacao(coeficienteVariacao: number): boolean {
  return coeficienteVariacao <= 30;
}

export function validarQuantidadeAmostras(
  numeroAmostras: number,
  grauDesejado: GrauFundamentacao
): boolean {
  switch (grauDesejado) {
    case GrauFundamentacao.III:
      return numeroAmostras >= 6;
    case GrauFundamentacao.II:
      return numeroAmostras >= 4;
    case GrauFundamentacao.I:
      return numeroAmostras >= 3;
    default:
      return false;
  }
}
